import { PlatformError, type DocStorePort, type DocTxRunner } from '@djgg/core';
import { assetGenJobDoc, buildJobDoc, versionDoc } from '../paths.js';
import type { AssetGenJob, BuildJob, CreationVersion, MeasuredBuildExecutionV1 } from '../types.js';
import type { BuildClaimAuthority } from './buildJobs.js';

/** Start a composed build's wall window at the claim that starts its first
 * request segment. Admission stamps a provisional deadline (acceptance +
 * window); the queue in front of the build is not the build's to spend, so
 * until a request segment has started, each claim re-anchors the window on
 * itself: the parent deadline and every media child's copy of it move
 * together, in one transaction, before any work or provider dispatch. Once a
 * segment has started, the window is fixed (`measuredBuildWindowStartAt` is
 * its start) and is returned unchanged. Spend is still bounded by the
 * accepted provider-call and cost limits, which never change. */
export async function startCompositionWindow(
  store: DocStorePort & Partial<DocTxRunner>,
  now: () => number,
  jobId: string,
  claim: BuildClaimAuthority,
): Promise<MeasuredBuildExecutionV1> {
  if (!store.runTransaction) throw new PlatformError('unavailable', 'Composition window start requires transactions');
  return store.runTransaction(async tx => {
    const job = await tx.get<BuildJob>(buildJobDoc(jobId));
    const run = job?.measuredExecution;
    const at = now();
    if (!job || job.status !== 'claimed' || job.workerId !== claim.workerId || job.claimToken !== claim.claimToken
      || job.leaseGeneration !== claim.leaseGeneration || (job.leaseExpiry ?? 0) <= at
      || !run?.compositionDigest || run.compositionV2Acceptance) {
      throw new PlatformError('conflict', 'Composition window start lost its claim or authority');
    }
    if (run.traceSegments.length) return structuredClone(run);
    if (run.checkpoint || run.compositionBarrier || run.usage.reservedProviderCalls || Object.keys(run.reservations).length) {
      throw new PlatformError('conflict', 'Composition run holds work outside any request segment');
    }
    const version = await tx.get<CreationVersion>(versionDoc(job.creationId, job.version));
    const childIds = [version?.creationRun?.visualJobId, version?.creationRun?.musicJobId]
      .filter((id): id is string => !!id);
    const deadlineAt = at + run.limits.maxWallTimeMs;
    const children: Array<{ id: string; job: AssetGenJob }> = [];
    for (const id of childIds) {
      const child = await tx.get<AssetGenJob>(assetGenJobDoc(id));
      if (child?.measuredBudget?.buildJobId !== jobId) continue;
      if (child.status !== 'awaiting-core' || child.measuredBudget.acceptedAt !== run.acceptedAt
        || child.measuredBudget.deadlineAt !== run.deadlineAt) {
        throw new PlatformError('conflict', 'Composition media child has a different window');
      }
      children.push({ id, job: child });
    }
    const started: MeasuredBuildExecutionV1 = { ...run, deadlineAt, ledgerRevision: (run.ledgerRevision ?? 0) + 1 };
    await tx.update(buildJobDoc(jobId), { measuredExecution: started });
    for (const { id, job: child } of children) {
      await tx.update(assetGenJobDoc(id), { measuredBudget: { ...child.measuredBudget!, deadlineAt } });
    }
    return structuredClone(started);
  });
}
